/**
 * @description Hook utilizado para manipular descrições técnicas dos vestígios
 * @author Diego Sabino Amorim de Araújo
 * @file useDescricaoTecnicaVestigioService.js
 * @license Copyright (c) 2023 SIGEP DIGETI ITEP/RN
 */

import { useFetch } from './useFetch'

export const useDescricaoTecnicaVestigioService = () => {
  const fetch = useFetch()

  const listarDescricoesTecnicasDeVestigoAsync = async (idVestigio, setProcessando) => {
    try {
      const response = await fetch.get(`/descricaoTecnica/deVestigio/paraExame/${idVestigio}`)
      return response.data.descricoesTecnicas
    } catch (err) {
      return null
    } finally {
      if (setProcessando) setProcessando(false)
    }
  }

  const listarDescricoesTecnicasAtivasDeVestigioAsync = async (idVestigio, setProcessando) => {
    try {
      if (setProcessando) setProcessando(true)
      const response = await fetch.get(`descricaoTecnica/deVestigio/ativas/paraExame/${idVestigio}`)
      return response.data.descricoesTecnicas
    } catch (err) {
      return null
    } finally {
      if (setProcessando) setProcessando(false)
    }
  }

  const listarDescricoesTecnicasAtivasDeExameSolicitadoComAnalisesConstatacaoAsync = async (idExameSolicitado, setProcessando) => {
    try {
      if (setProcessando) setProcessando(true)
      const response = await fetch.get(`descricaoTecnica/deExameSolicitado/ativas/comAnalisesConstatacoes/paraExame/${idExameSolicitado}`)
      return response.data.descricoesTecnicas
    } catch (err) {
      return null
    } finally {
      if (setProcessando) setProcessando(false)
    }
  }

  const listarDescricoesTecnicasAtivasComAnalisesDeExameQuimicoAsync = async (idExameSolicitado) => {
    try {
      const response = await fetch.get(`descricaoTecnica/deExameSolicitado/ativas/quimico/comAnalises/${idExameSolicitado}`)
      return response.data.descricoesTecnicas
    } catch (err) {
      return null
    }
  }

  const listarDescricoesTecnicasAtivasDeExameSolicitadoComAnalisesEficienciaAsync = async (idExameSolicitado, setProcessando) => {
    try {
      if (setProcessando) setProcessando(true)
      const response = await fetch.get(`descricaoTecnica/deExameSolicitado/ativas/comAnalisesEficiencia/paraExame/${idExameSolicitado}`)
      return response.data.descricoesTecnicas
    } catch (err) {
      return null
    } finally {
      if (setProcessando) setProcessando(false)
    }
  }

  const listarDescricoesTecnicasAtivasDeExameSolicitadoComColetaPadraoHabilitadaAsync = async (idExameSolicitado, setProcessando) => {
    try {
      if (setProcessando) setProcessando(true)
      const response = await fetch.get(`descricaoTecnica/deExameSolicitado/ativas/comColetaPadrao/paraExame/${idExameSolicitado}`)
      return response.data.descricoesTecnicas
    } catch (err) {
      return null
    } finally {
      if (setProcessando) setProcessando(false)
    }
  }

  const adicionarVestigioExtraidoDescricaoVestigioBalisticoAsync = async (idDescricaoTecnica, idVestigio, setProcessando) => {
    try {
      if (setProcessando) setProcessando(true)
      const response = await fetch.put(`descricaoTecnica/balistico/${idDescricaoTecnica}/${idVestigio}`)
      return response.status
    } catch (err) {
      return null
    } finally {
      if (setProcessando) setProcessando(false)
    }
  }

  const listarDescricaoTecnicaVestigioDadosEspecificosAsync = async (id, identificadorTipoVestigio, setProcessando) => {
    try {
      if (setProcessando) setProcessando(true)
      const response = await fetch.get(`/descricaoTecnica/dadosEspecificos/paraExame/${id}/${identificadorTipoVestigio}`)
      return response.data.descricaoTecnica
    } catch (err) {
      return null
    } finally {
      if (setProcessando) setProcessando(false)
    }
  }

  const listarDescricoesVestigiosBalisticosNaoArtesanaisESemNumeracaoDeExameAsync = async (idExame, setProcessando) => {
    try {
      if (setProcessando) setProcessando(true)
      const response = await fetch.get(`/descricaoTecnica/deExame/ativoParaExameQuimicoMetalografico/${idExame}`)
      return response.data.descricoesVestigiosBalisticos
    } catch (error) {
      return null
    } finally {
      if (setProcessando) setProcessando(false)
    }
  }

  const listarDescricaoTecnicaVestigioQuimicoDeExameSolicitadoAsync = async (idExameSolicitado) => {
    try {
      const response = await fetch.get(`/descricaoTecnica/deExameSolicitado/ativas/descricaoVestigioQuimico/${idExameSolicitado}`)
      return response.data.descricoesTecnicas
    } catch (err) {
      return null
    }
  }

  const cadastrarDescricaoTecnicaVestigioService = async (tipoVestigio, idVestigio, idExameRealizado, dados, setProcessando) => {
    try {
      if (setProcessando) setProcessando(true)
      const commonData = {
        tipoVestigioGenerico: dados.tipoVestigioGenerico,
        tipoVestigioEspecifico: dados.tipoVestigioEspecifico,
        descricao: dados.descricao,
        unidades: dados.unidades,
        unidadesConsumidas: dados.unidadesConsumidas,
        arquivos: dados.arquivos,
      }

      const getUrl = (tipo) => `/descricaoTecnica/${tipo}/${idVestigio}/${idExameRealizado}`
      switch (tipoVestigio) {
        case 'QUIMICO': {
          const dadosDescricaoVestigioQuimico = {
            ...commonData,
            quantidade: dados.quantidade,
            unidadeQuantidade: dados.unidadeQuantidade,
            tipoQuantidade: dados.tipoQuantidade,
            quantificacaoContraprova: dados.quantificacaoContraprova,
            quantidadeContraprova: dados.quantidadeContraprova,
            unidadeQuantidadeContraprova: dados.unidadeQuantidadeContraprova,
            lacreContraprova: dados.lacreContraprova,
            anexo: dados.anexo === 'SIM' || false,
            lacreAnexo: dados.lacreAnexo,
            tipoEmbalagemPrimaria: dados.tipoEmbalagemPrimaria,
            outroTipoEmbalagemPrimaria: dados.outroTipoEmbalagemPrimaria,
            corEmbalagemPrimaria: dados.corEmbalagemPrimaria,
            outraCorEmbalagemPrimaria: dados.outraCorEmbalagemPrimaria,
            logomarcaEmbalagemPrimaria: dados.logomarcaEmbalagemPrimaria === 'SIM' || false,
            tipoLogomarcaEmbalagemPrimaria: dados.tipoLogomarcaEmbalagemPrimaria,
            tipoFechoEmbalagemPrimaria: dados.tipoFechoEmbalagemPrimaria,
            outroTipoFechoEmbalagemPrimaria: dados.outroTipoFechoEmbalagemPrimaria,
            corMaterial: dados.corMaterial,
            outraCorMaterial: dados.outraCorMaterial,
            logomarcaSubstancia: dados.logomarcaSubstancia === 'SIM' || false,
            tipoLogomarcaSubstancia: dados.tipoLogomarcaSubstancia,
            outroAspectoFisico: dados.outroAspectoFisico,
            aspFisics: dados.aspFisics ? dados.aspFisics.map((aspectoFisico) => aspectoFisico.id) : null,
          }
          return await fetch.postOpen(getUrl('quimico'), dadosDescricaoVestigioQuimico)
        }

        case 'BALISTICO': {
          const dadosDescricaoVestigioBalistico = {
            ...commonData,
            //ARMA DE FOGO
            tipo: dados.tipo || null,
            marca: dados.marca || null,
            modelo: dados.modelo || null,
            calibreNominal: dados.calibreNominal || null,
            calibreCompativel: dados.calibreCompativel || null,
            locFabs: dados.locFabs?.nome?.toUpperCase() === 'OUTRO' ? dados.localFabricacaoInput : dados.locFabs?.map((localFabricacao) => localFabricacao.id) || null,
            localFabricacaoInput: dados.localFabricacaoInput || null,
            numeracaoDeSerie: dados.numeracaoDeSerie || null,
            locGravNumSers: dados.locGravNumSers?.nome?.toUpperCase() === 'OUTRO' ? dados.localGravacaoNumeracaoSerieInput : dados.locGravNumSers?.map((localGravNumS) => localGravNumS.id) || null,
            localGravacaoNumeracaoSerieInput: dados.localGravacaoNumeracaoSerieInput || null,
            numeracaoDeMontagem: dados.numeracaoDeMontagem || null, // aqui
            locGravNumMons: dados.locGravNumMons?.nome?.toUpperCase() === 'OUTRO' ? dados.localGravacaoNumeracaoMontagemInput : dados.locGravNumMons?.map((localGravNumS) => localGravNumS.id) || null,
            localGravacaoNumeracaoMontagemInput: dados.localGravacaoNumeracaoMontagemInput || null,
            sisPercs: dados.sisPercs?.nome?.toUpperCase() === 'OUTRO' ? dados.sistemaPercussaoInput : dados.sisPercs?.map((sistemaPercussao) => sistemaPercussao.id) || null,
            sistemaPercussaoInput: dados.sistemaPercussaoInput || null,
            sisCarrs: dados.sisCarrs?.nome?.toUpperCase() === 'OUTRO' ? dados.sistemaCarregamentoInput : dados.sisCarrs?.map((sistemaCarregamento) => sistemaCarregamento.id) || null,
            sistemaFuncionamento: dados.sistemaFuncionamento || null,
            sistemaCarregamentoInput: dados.sistemaCarregamentoInput || null,
            almaCanos: dados.almaCanos?.nome?.toUpperCase() === 'OUTRO' ? dados.almaCanoInput : dados.almaCanos?.map((almaCano) => almaCano.id) || null,
            almaCanoInput: dados.almaCanoInput || null,
            comprimentoCano: dados.comprimentoCano || null,
            acabamento: dados.acabamento || null,
            tambores: dados.tambores?.nome?.toUpperCase() === 'OUTRO' ? dados.tamborInput : dados.tambores?.map((tambor) => tambor.id) || null,
            tamborInput: dados.tamborInput || null,
            carregadores: dados.carregadores?.nome?.toUpperCase() === 'OUTRO' ? dados.carregadorInput : dados.carregadores?.map((carregador) => carregador.id) || null,
            carregadorInput: dados.carregadorInput || null,
            capacidade: dados.capacidade || null,
            mecanDisps: dados.mecanDisps?.nome?.toUpperCase() === 'OUTRO' ? dados.mecanismoDisparoInput : dados.mecanDisps?.map((mecanismoDisparo) => mecanismoDisparo.id) || null,
            mecanismoDisparoInput: dados.mecanismoDisparoInput || null,
            massaMira: dados.massaMira || null,
            alcaMira: dados.alcaMira || null,
            empunhaduras: dados.empunhaduras?.nome?.toUpperCase() === 'OUTRO' ? dados.empunhaduraInput : dados.empunhaduras?.map((empunhadura) => empunhadura.id) || null,
            empunhaduraInput: dados.empunhaduraInput || null,
            coronhas: dados.coronhas?.nome?.toUpperCase() === 'OUTRO' ? dados.coronhaInput : dados.coronhas?.map((coronha) => coronha.id) || null,
            coronhaInput: dados.coronhaInput || null,
            telhas: dados.telhas?.nome?.toUpperCase() === 'OUTRO' ? dados.telhaInput : dados.telhas?.map((telha) => telha.id) || null,
            telhaInput: dados.telhaInput || null,
            guardaMao: dados.guardaMao || null,
            gravacoes: dados.gravacoes?.nome?.toUpperCase() === 'OUTRO' ? dados.gravacoesInput : dados.gravacoes?.map((gravacao) => gravacao.id) || null,
            gravacoesInput: dados.gravacoesInput || null,
            outrasCaracteristicas: dados.outrasCaracteristicas || null,
            tipoFabricacao: dados.tipoFabricacao || null,
            coletaPadrao: dados.coletaPadrao || null,
            estadoConservacao: dados.estadoConservacao || null,

            // CARTUCHO
            calibreVariacao: dados.calibreVariacao || null,
            fabricante: dados.fabricante || null,
            capsulaEsps: dados.capsulaEsps?.map((capsula) => capsula.id) || null,
            capsulaEspoletamentoInput: dados.capsulaEspoletamentoInput || null,
            tipoEstojos: dados.tipoEstojos?.map((te) => te.id) || null,
            tipoEstojoInput: dados.tipoEstojoInput || null,
            tiposProjetil: dados.tiposProjetil?.map((tp) => tp.id) || null,
            tipoProjetilInput: dados.tipoProjetilInput || null,
            numeroLote: dados.numeroLote || null,
            formatoPercutor: dados.formatoPercutor || null,

            // ELEMENTO DE MUNIÇÃO
            ressaltosCavados: dados.ressaltosCavados?.map((rc) => rc.id) || null,
            ressaltosCavadosInput: dados.ressaltosCavadosInput || null,
            tiposRaiamentos: dados.tiposRaiamentos?.map((tr) => tr.id) || null,
            tiposRaiamentosInput: dados.tiposRaiamentosInput || null,
            medidas: dados.medidas || null,
            medidaMassa: dados.medidaMassa || null,
            medidaDiametro: dados.medidaDiametro || null,
            medidaComprimento: dados.medidaComprimento || null,

            // ELEMENTO DE RECARGA
            embalagemPrimaria: dados.embalagemPrimaria || null,
            quantidade: dados.quantidade || null,
            unidadeQuantidade: dados.unidadeQuantidade || null,
          }
          return await fetch.postOpen(getUrl('balistico'), dadosDescricaoVestigioBalistico, dados)
        }

        case 'BIOLOGICO': {
          const dadosDescricaoVestigioBiologico = {
            ...commonData,
          }
          return await fetch.postOpen(getUrl('biologico'), dadosDescricaoVestigioBiologico, dados)
        }

        case 'DOCUMENTAL': {
          const dadosDescricaoVestigioDocumental = {
            ...commonData,
          }
          return await fetch.postOpen(getUrl('documental'), dadosDescricaoVestigioDocumental, dados)
        }

        case 'GERAL': {
          const dadosDescricaoVestigioGeral = {
            ...commonData,
          }
          return await fetch.postOpen(getUrl('geral'), dadosDescricaoVestigioGeral, dados)
        }

        case 'ELETRONICO': {
          const dadosDescricaoVestigioEletronico = {
            ...commonData,
          }
          return await fetch.postOpen(getUrl('eletronico'), dadosDescricaoVestigioEletronico, dados)
        }

        case 'PAPILOSCOPICO': {
          const dadosDescricaoVestigioPapiloscopico = {
            ...commonData,
          }
          return await fetch.postOpen(getUrl('papiloscopico'), dadosDescricaoVestigioPapiloscopico, dados)
        }

        case 'MERCEOLOGICO': {
          const dadosDescricaoVestigioMerceologico = {
            ...commonData,
          }
          return await fetch.postOpen(getUrl('merceologico'), dadosDescricaoVestigioMerceologico, dados)
        }

        default:
          return null
      }
    } catch (err) {
      console.log(err)
    } finally {
      if (setProcessando) setProcessando(false)
    }
  }

  const deletarDescricaoTecnicaVestigio = async (id, setProcessando) => {
    try {
      if (setProcessando) setProcessando(true)
      await fetch.del(`/descricaoTecnica/deExame/${id}`)
    } catch (err) {
      return
    } finally {
      if (setProcessando) setProcessando(false)
    }
  }

  const inativarDescricaoTecnicaVestigio = async (id, setProcessando) => {
    try {
      if (setProcessando) setProcessando(true)
      await fetch.del(`/descricaoTecnica/deExame/inativar/${id}`)
    } catch (err) {
      return
    } finally {
      if (setProcessando) setProcessando(false)
    }
  }

  return {
    listarDescricoesTecnicasDeVestigoAsync,
    listarDescricoesTecnicasAtivasDeVestigioAsync,
    listarDescricoesTecnicasAtivasDeExameSolicitadoComAnalisesConstatacaoAsync,
    cadastrarDescricaoTecnicaVestigioService,
    listarDescricaoTecnicaVestigioDadosEspecificosAsync,
    deletarDescricaoTecnicaVestigio,
    inativarDescricaoTecnicaVestigio,
    listarDescricoesTecnicasAtivasDeExameSolicitadoComAnalisesEficienciaAsync,
    listarDescricoesVestigiosBalisticosNaoArtesanaisESemNumeracaoDeExameAsync,
    listarDescricaoTecnicaVestigioQuimicoDeExameSolicitadoAsync,
    listarDescricoesTecnicasAtivasComAnalisesDeExameQuimicoAsync,
    listarDescricoesTecnicasAtivasDeExameSolicitadoComColetaPadraoHabilitadaAsync,
    adicionarVestigioExtraidoDescricaoVestigioBalisticoAsync,
  }
}
